/**
 * PMV-1.5 — who may move a project's stage DIRECTLY, rather than request it.
 *
 * ⚠️ THIS DECIDES WHAT IS SHOWN, NOT WHAT IS ALLOWED. The database's two stage
 * triggers (`check_stage_update_permission`, `restrict_current_stage_id_update`,
 * last defined in `20260817a_sub_1_21_f40_stage_triggers_read_both_paths.sql`)
 * are the rule, and they re-check every move regardless of what this returns.
 *
 * A direct move is offered when ONE held role both:
 *
 * 1. is in the triggers' allowed set, so the move cannot be refused, and
 * 2. carries a management permission, so a rep is still sent to Request move.
 *
 * Both halves are needed. The triggers alone admit Sales Rep, Sales Manager and
 * Finance, which would bypass PMV-1.4's request flow. The permissions alone admit
 * Installer PM Manager (6), which holds `manage_projects` and is refused by the
 * database. Measured 2026-10-30.
 *
 * ⚠️ PER ROLE, NOT PER USER. `hasPermission()` is the union over every account a
 * user holds a role in, so a Sales Rep who is Admin elsewhere would pass a
 * union check in the account where they are only a rep.
 *
 * Replaces a list of role names in `Pipeline.tsx` / `PipelineResi.tsx` that read
 * one role only (the legacy `users.role_id`, else the first `user_roles` row), and
 * `edit_projects` in `ProjectModal.tsx`, which every role holds.
 */

/** The triggers' `v_allowed`. Keep in step with the migration named above. */
export const STAGE_CHANGE_ALLOWED_ROLE_IDS: readonly number[] = [1, 2, 3, 4, 7, 36, 37, 42, 43, 44];

export const DIRECT_STAGE_MOVE_PERMISSIONS: readonly string[] = [
  'manage_projects',
  'project.manage_all',
  'system.override',
];

export interface RoleGrant {
  role_id: number;
  permission: string;
}

export interface StageMoveGateInput {
  /** `users.role_id`. The triggers read it without a tenant check, so neither does this. */
  legacyRoleId: number | null;
  /** `user_roles` rows IN THE CURRENT ACCOUNT only, matching the triggers' tenant scope. */
  tenantRoleIds: readonly number[];
  grants: readonly RoleGrant[];
}

export function canMoveStageDirectly({ legacyRoleId, tenantRoleIds, grants }: StageMoveGateInput): boolean {
  const held = new Set<number>(tenantRoleIds);
  if (legacyRoleId != null) held.add(legacyRoleId);

  return [...held].some(
    (roleId) =>
      STAGE_CHANGE_ALLOWED_ROLE_IDS.includes(roleId) &&
      grants.some((g) => g.role_id === roleId && DIRECT_STAGE_MOVE_PERMISSIONS.includes(g.permission)),
  );
}
